import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button.tsx";
import { useState, useEffect, useCallback } from "react";
import { Loader2 } from "lucide-react";
import { validateRegistrationForm } from "@/lib/utils/register";
import { checkPasswordStrength } from "@/lib/utils";
import { debounce } from "@/lib/utils";
import { CountryCodeSelector } from "@/components/auth/country-code-selector";
import { NewPasswordInput } from "@/components/auth/new-password-input";

interface RegisterFormProps {
  onRegister: (
    email: string,
    password: string,
    phoneNumber: string,
    countryCode: string,
  ) => void;
}

export function RegisterForm({ onRegister }: RegisterFormProps) {
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [passwordStrength, setPasswordStrength] = useState<ReturnType<
    typeof checkPasswordStrength
  > | null>(null);
  const [isFormValid, setIsFormValid] = useState(false);
  const [countryCode, setCountryCode] = useState("+1");
  const [hasInteracted, setHasInteracted] = useState(false);

  const validateForm = useCallback(
    debounce(() => {
      const validation = validateRegistrationForm(
        email,
        phoneNumber,
        password,
        confirmPassword,
        passwordStrength,
      );
      setIsFormValid(validation.isValid);
      if (!validation.isValid && hasInteracted) {
        setError(validation.error || null);
      } else {
        setError(null);
      }
    }, 300),
    [email, phoneNumber, password, confirmPassword, passwordStrength, hasInteracted],
  );

  useEffect(() => {
    validateForm();
  }, [validateForm]);

  const handleRegister = async () => {
    const validation = validateRegistrationForm(
      email,
      phoneNumber,
      password,
      confirmPassword,
      passwordStrength,
    );

    if (!validation.isValid) {
      setError(validation.error || null);
      return;
    }

    setLoading(true);
    try {
      await onRegister(email, password, phoneNumber, countryCode);
    } catch (e) {
      console.error(e);
      setError("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChange = (newPassword: string) => {
    setPassword(newPassword);
    setPasswordStrength(checkPasswordStrength(newPassword));
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center gap-2">
        <CountryCodeSelector value={countryCode} onChange={setCountryCode} />
        <Input
          value={phoneNumber}
          onChange={(e) => {
            setPhoneNumber(e.target.value);
            if (!hasInteracted) setHasInteracted(true);
          }}
          placeholder="Enter your phone number"
          className="h-12 flex-1 rounded-[12px] border-[#e3dbcf] bg-[#faf4ea] text-sm placeholder:text-[#b4aa9a] focus-visible:ring-2 focus-visible:ring-black/5"
        />
      </div>
      <Input
        value={email}
        onChange={(e) => {
          setEmail(e.target.value);
          if (!hasInteracted) setHasInteracted(true);
        }}
        placeholder="Enter your email"
        className="h-12 rounded-[12px] border-[#e3dbcf] bg-[#faf4ea] text-sm placeholder:text-[#b4aa9a] focus-visible:ring-2 focus-visible:ring-black/5"
      />
      <NewPasswordInput
        value={password}
        onChange={handlePasswordChange}
        confirmValue={confirmPassword}
        onConfirmChange={setConfirmPassword}
        placeholder="Enter your password"
        confirmPlaceholder="Confirm your password"
        showConfirmation={true}
        className="rounded-[12px] border-[#e3dbcf] bg-[#faf4ea] text-sm placeholder:text-[#b4aa9a] focus-visible:ring-2 focus-visible:ring-black/5"
      />

      <Button
        onClick={handleRegister}
        className="h-12 w-full rounded-[12px] bg-black text-sm font-semibold text-white hover:bg-black/90"
        disabled={loading || !isFormValid}
      >
        {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
        Register
      </Button>

      {error && <div className="text-sm text-red-500">{error}</div>}
    </div>
  );
}
